import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Phone, 
  PhoneIncoming, 
  PhoneOutgoing, 
  PhoneMissed, 
  Video, 
  VideoOff, 
  Search, 
  Clock, 
  Calendar,
  MoreVertical,
  Trash2,
  MessageSquare,
  Info,
  Loader2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ShieldCheck,
  Activity
} from 'lucide-react';
import { useCall } from './CallProvider';
import { useCallHistory, CallHistoryItem } from '@/hooks/useCallHistory';
import { useTranslation } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';

export function CallHistory() {
  const { startCall } = useCall();
  const { callHistory, loading, error, insights } = useCallHistory();
  const { t } = useTranslation();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'missed' | 'audio' | 'video'>('all');

  const deepLinkCallId = useMemo(() => {
    // Prefer navigation state (Messages -> CallHistory), but also support query param.
    const stateCallId = (location.state as any)?.callId;
    if (typeof stateCallId === 'string' && stateCallId.trim()) return stateCallId.trim();

    const params = new URLSearchParams(location.search);
    const queryCallId = params.get('call');
    if (typeof queryCallId === 'string' && queryCallId.trim()) return queryCallId.trim();

    return undefined;
  }, [location.search, location.state]);

  const [highlightedCallId, setHighlightedCallId] = useState<string | undefined>(undefined);

  const formatDuration = (seconds: number) => {
    if (seconds === 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60));
      return t('calls.minutesAgo', { count: diffInMinutes });
    } else if (diffInHours < 24) {
      return t('calls.hoursAgo', { count: diffInHours });
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return t('calls.daysAgo', { count: diffInDays });
    }
  };

  const formatHourLabel = (hour: number) => {
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const normalized = hour % 12 || 12;
    return `${normalized} ${suffix}`;
  };

  const getCallIcon = (item: CallHistoryItem) => {
    const iconClass = "w-4 h-4";
    
    if (item.status === 'missed') {
      return <PhoneMissed className={cn(iconClass, "text-red-500")} />;
    }
    
    if (item.direction === 'incoming') {
      return item.type === 'video' ? 
        <Video className={cn(iconClass, "text-green-500")} /> : 
        <PhoneIncoming className={cn(iconClass, "text-green-500")} />;
    } else {
      return item.type === 'video' ? 
        <Video className={cn(iconClass, "text-blue-500")} /> : 
        <PhoneOutgoing className={cn(iconClass, "text-blue-500")} />;
    }
  };

  const getStatusBadge = (status: CallHistoryItem['status']) => {
    const variants = {
      completed: 'bg-green-100 text-green-800',
      missed: 'bg-red-100 text-red-800',
      rejected: 'bg-orange-100 text-orange-800',
      failed: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <Badge className={cn("text-xs", variants[status])}>
        {t(`calls.${status}`)}
      </Badge>
    );
  };

  const filteredHistory = callHistory.filter(item => {
    const matchesSearch = item.contact.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || 
      (filter === 'missed' && item.status === 'missed') ||
      (filter === 'audio' && item.type === 'audio') ||
      (filter === 'video' && item.type === 'video');
    
    return matchesSearch && matchesFilter;
  });

  useEffect(() => {
    if (!deepLinkCallId || loading || error) return;

    // Only attempt once the call exists in the (filtered) list.
    const exists = filteredHistory.some((c) => c.id === deepLinkCallId);
    if (!exists) return;

    setHighlightedCallId(deepLinkCallId);

    // Ensure the DOM renders before scrolling.
    const id = deepLinkCallId;
    const raf = window.requestAnimationFrame(() => {
      const el = document.querySelector(`[data-call-id="${CSS.escape(id)}"]`) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });

    const timeout = window.setTimeout(() => {
      setHighlightedCallId((current) => (current === id ? undefined : current));
    }, 3500);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
    };
    // Intentionally depends on filteredHistory so it triggers after history loads.
  }, [deepLinkCallId, loading, error, filteredHistory]);

  const handleCallBack = (item: CallHistoryItem) => {
    startCall(
      item.contact.id,
      item.contact.name,
      item.contact.avatar,
      item.type
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <style>
        {`
          @keyframes callHighlightPulse {
            0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.0); }
            25% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.25); }
            60% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.12); }
            100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.0); }
          }
        `}
      </style>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('calls.callHistory')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('calls.viewAndManageCalls')}</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Trash2 className="w-4 h-4 mr-2" />
            {t('calls.clearAllHistory')}
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="p-12 flex flex-col items-center justify-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">{t('calls.loadingCallHistory')}</p>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-600">
              <Info className="w-5 h-5" />
              <div>
                <p className="font-semibold">{t('calls.errorLoadingHistory')}</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Total Calls</p>
                <p className="text-xl font-semibold">{insights.totalCalls}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Completed</p>
                <p className="text-xl font-semibold text-green-600">{insights.completedCalls}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Missed</p>
                <p className="text-xl font-semibold text-red-600">{insights.missedCalls}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Avg Duration</p>
                <p className="text-xl font-semibold">{formatDuration(insights.averageDuration)}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-gray-500">Relationship Health</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Reliability score across active contacts
                </p>
              </div>
              <div className={cn(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
                insights.relationshipHealthScore >= 70
                  ? 'bg-green-100 text-green-700'
                  : insights.relationshipHealthScore >= 45
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
              )}>
                <ShieldCheck className="w-3 h-3" />
                {insights.relationshipHealthScore}/100
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Weekly Call Momentum</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {insights.thisWeekCalls} this week vs {insights.previousWeekCalls} last week
                </p>
              </div>
              <div className={cn(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
                insights.weeklyTrendPercent >= 0
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              )}>
                {insights.weeklyTrendPercent >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {insights.weeklyTrendPercent > 0 ? '+' : ''}{insights.weeklyTrendPercent}%
              </div>
            </CardContent>
          </Card>

          {(insights.topContacts.length > 0 || insights.missedCallbacks.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Smart Insights</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {insights.topContacts.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Frequent Contacts</p>
                    <div className="flex flex-wrap gap-2">
                      {insights.topContacts.slice(0, 4).map((contact) => (
                        <Button
                          key={`top-${contact.contactId}`}
                          variant="outline"
                          size="sm"
                          onClick={() => startCall(contact.contactId, contact.name, contact.avatar, 'audio')}
                        >
                          <Phone className="w-3 h-3 mr-1" />
                          {contact.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {insights.missedCallbacks.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Missed Callback Queue</p>
                    <div className="flex flex-wrap gap-2">
                      {insights.missedCallbacks.slice(0, 4).map((contact) => (
                        <Button
                          key={`missed-${contact.contactId}`}
                          variant="outline"
                          size="sm"
                          className="border-red-200 text-red-700 hover:bg-red-50"
                          onClick={() => startCall(contact.contactId, contact.name, contact.avatar, 'audio')}
                        >
                          <PhoneMissed className="w-3 h-3 mr-1" />
                          {contact.name} ({contact.missedCount})
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {insights.highPriorityFollowUps.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Priority Follow-ups
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {insights.highPriorityFollowUps.slice(0, 4).map((item) => (
                    <div key={`follow-up-${item.contactId}`} className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-amber-900">{item.name}</p>
                        <p className="text-xs text-amber-700">
                          {item.missedCount} missed • priority {item.priorityScore}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-300 text-amber-800 hover:bg-amber-100"
                        onClick={() => startCall(item.contactId, item.name, item.avatar, 'audio')}
                      >
                        <Phone className="w-3 h-3 mr-1" />
                        Call now
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {insights.reliabilityContacts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-500" />
                  Contact Reliability Radar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {insights.reliabilityContacts.slice(0, 4).map((contact) => (
                    <div
                      key={`reliability-${contact.contactId}`}
                      className="flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-blue-900">{contact.name}</p>
                        <p className="text-xs text-blue-700">
                          reliability {contact.reliabilityScore} • risk {contact.reconnectRiskScore} • response {contact.responseRate}%
                          {contact.nextBestHour !== null ? ` • best hour ${formatHourLabel(contact.nextBestHour)}` : ''}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-300 text-blue-800 hover:bg-blue-100"
                        onClick={() => startCall(contact.contactId, contact.name, contact.avatar, 'audio')}
                      >
                        <Phone className="w-3 h-3 mr-1" />
                        Reconnect
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {insights.hourlyActivity.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Best Time To Call</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {insights.bestCallHour !== null && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                    Highest completion window: {formatHourLabel(insights.bestCallHour)}
                  </div>
                )}

                <div className="space-y-2">
                  {insights.hourlyActivity
                    .slice()
                    .sort((a, b) => {
                      const aRate = a.total > 0 ? a.completed / a.total : 0;
                      const bRate = b.total > 0 ? b.completed / b.total : 0;
                      if (bRate !== aRate) {
                        return bRate - aRate;
                      }
                      return b.total - a.total;
                    })
                    .slice(0, 5)
                    .map((slot) => {
                      const completionRate = slot.total > 0 ? Math.round((slot.completed / slot.total) * 100) : 0;
                      return (
                        <div key={`hour-${slot.hour}`} className="space-y-1">
                          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
                            <span>{formatHourLabel(slot.hour)}</span>
                            <span>{completionRate}% completion ({slot.total})</span>
                          </div>
                          <div className="h-2 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-cyan-500"
                              style={{ width: `${completionRate}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search and Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder={t('calls.searchCallHistory')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant={filter === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter('all')}
                  >
                    {t('calls.all')}
                  </Button>
                  <Button
                    variant={filter === 'missed' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter('missed')}
                  >
                    <PhoneMissed className="w-3 h-3 mr-1" />
                    {t('calls.missed')}
                  </Button>
                  <Button
                    variant={filter === 'audio' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter('audio')}
                  >
                    <Phone className="w-3 h-3 mr-1" />
                    {t('calls.audio')}
                  </Button>
                  <Button
                    variant={filter === 'video' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter('video')}
                  >
                    <Video className="w-3 h-3 mr-1" />
                    {t('calls.video')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Call History List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {t('calls.recentCalls')} ({filteredHistory.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredHistory.map((item) => (
                  <div 
                    key={item.id}
                    data-call-id={item.id}
                    className={cn(
                      'p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group',
                      highlightedCallId === item.id && 'bg-blue-50/70 dark:bg-blue-900/20',
                      highlightedCallId === item.id && 'ring-2 ring-blue-400/60 ring-inset',
                      highlightedCallId === item.id && 'animate-[callHighlightPulse_1.6s_ease-in-out_2]'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <Avatar>
                            <AvatarImage src={item.contact.avatar} />
                            <AvatarFallback>{item.contact.name[0]}</AvatarFallback>
                          </Avatar>
                          
                          {/* Call type indicator */}
                          <div className="absolute -bottom-1 -right-1 bg-white dark:bg-gray-800 rounded-full p-1">
                            {getCallIcon(item)}
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-gray-900 dark:text-white truncate">
                              {item.contact.name}
                            </p>
                            {getStatusBadge(item.status)}
                          </div>
                          
                          <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatTimestamp(item.timestamp)}
                            </span>
                            {item.duration > 0 && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDuration(item.duration)}
                              </span>
                            )}
                            <span className="capitalize">
                              {t(`calls.${item.direction}`)} {t(`calls.${item.type}`)} {t('calls.title').toLowerCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCallBack(item)}
                          title={t('calls.callBackWith', { type: item.type })}
                        >
                          {item.type === 'video' ? (
                            <Video className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Phone className="w-4 h-4 text-green-600" />
                          )}
                        </Button>
                        
                        <Button size="sm" variant="ghost">
                          <MessageSquare className="w-4 h-4 text-gray-600" />
                        </Button>
                        
                        <Button size="sm" variant="ghost">
                          <MoreVertical className="w-4 h-4 text-gray-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {filteredHistory.length === 0 && (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                    <Phone className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t('calls.noCallsFound')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {searchQuery ? t('calls.tryAdjustingSearch') : t('calls.historyWillAppear')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
